/**
 * 资源准入审核 · 列表与新建（三级准入的入口）。
 *
 * ══ 这一屏要守住的东西 ══
 *
 * 原型只给了一句机制：「绝不衔接未经尽调验证的资源」→
 * 机制为「三级准入审核 + 实地核验 + 持续动态评估 + 不合格主体清退」。
 * 因此列表的重点不是「有多少机构」，而是**每个主体现在到哪一步、能不能约**：
 *
 *   1. **计数全部来自接口**（`summary`）：界面不自己 filter 一遍再显示数字。
 *      前端自算的数字一旦与接口口径漂移，运营就会按错的数量排期；
 *   2. **可约状态必须带原因**：「不可约」三个字无法区分「还在审」与「已清退」，
 *      两者的下一步完全不同（继续推进 / 不得再接入）；
 *   3. **复评紧迫度用接口判定**（`reviewOverdue` / `reviewSoon` / `daysUntilNextReview`）：
 *      持续动态评估逾期本身就是不合格信号，不能只显示一个日期让人自己算；
 *   4. **三级文案随接口下发**（`tiers`）：每级审什么属于平台侧定义，
 *      集中在 `core/resourceAdmission.ts` 的 `tierDefinitions`，界面不另写一份；
 *   5. 403 是权限模型的正常结果（审核员之外的角色读不到），**照实显示服务端原文**，
 *      不渲染成「没有被审主体」——后者会让人以为平台一家机构都没登记。
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, ApiError } from '../lib/api.ts';
import type { ResourceSubjectsResult } from '../lib/api.ts';
import { useLoad } from '../lib/useLoad.ts';
import { Badge, Card, Kpi, PageHead, PermissionBlock } from '../components/ui.tsx';
import { EmptyBox, ErrorBox, LoadingBox, errorText } from '../components/StateBox.tsx';
import {
  MISSING_TEXT,
  admissionStatusTone,
  bookableNotice,
  formatDateTime,
  reviewUrgencyNotice,
} from '../lib/format.ts';

/** 状态筛选：取值即接口的 `status` 枚举，标签与 `admissionStatusLabels` 同一张表（避免两处漂移）。 */
const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: '全部状态' },
  { value: 'draft', label: '待提交' },
  { value: 'tier1_passed', label: '一级已通过' },
  { value: 'tier2_passed', label: '二级已通过' },
  { value: 'admitted', label: '已准入' },
  { value: 'listed', label: '已上架可约' },
  { value: 'rejected', label: '已否决' },
  { value: 'suspended', label: '已暂停' },
  { value: 'revoked', label: '已清退' },
];

/**
 * 级次筛选。**语义是「已完成至该级次及以上」**（接口按 `completedTiers >= tier` 过滤），
 * 因此标签必须这么写 —— 写成「第 2 级」会被读成「只筛第 2 级」。
 */
const TIER_FILTERS: { value: string; label: string }[] = [
  { value: '', label: '全部级次' },
  { value: '1', label: '已完成至 1 级及以上' },
  { value: '2', label: '已完成至 2 级及以上' },
  { value: '3', label: '已完成至 3 级（三级全通过）' },
];

interface Toast {
  text: string;
  tone: 'ok' | 'error';
}

/**
 * 新建待审主体。
 *
 * 新建后状态是 `draft`（待提交），**因此不可约** —— 这一点必须由界面说出来：
 * 「已登记」与「已可约」之间隔着三级审核，运营最容易在这里误解。
 * 下一步做什么由服务端 `nextStep` 给出，界面不自己拼。
 */
function CreateSubjectForm({
  categories,
  onDone,
}: {
  categories: ResourceSubjectsResult['categories'];
  onDone: (toast: Toast) => void;
}) {
  const [name, setName] = useState('');
  const [subjectType, setSubjectType] = useState('institution');
  const [category, setCategory] = useState(categories[0]?.key ?? '');
  const [city, setCity] = useState('');
  const [licenseNo, setLicenseNo] = useState('');
  const [licenseExpiresAt, setLicenseExpiresAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      const result = await adminApi.createResourceSubject({
        name: name.trim(),
        subjectType: subjectType === 'expert' ? 'expert' : 'institution',
        category,
        ...(city.trim() ? { city: city.trim() } : {}),
        ...(licenseNo.trim() ? { licenseNo: licenseNo.trim() } : {}),
        ...(licenseExpiresAt ? { licenseExpiresAt } : {}),
      });
      onDone({
        text:
          `已新建「${result.item.name}」（${result.item.subjectTypeLabel} · ${result.item.categoryName}）：` +
          `状态 ${result.item.statusLabel} · ${result.item.completedTierText} · ${bookableNotice(result.item).text}。` +
          result.nextStep,
        tone: 'ok',
      });
      setName('');
      setCity('');
      setLicenseNo('');
      setLicenseExpiresAt('');
    } catch (err) {
      // 服务端原文照实呈现（400 参数不合法 / 403 权限 / 429 限流都带着「为什么」）
      setError(err instanceof ApiError ? `新建被拒绝（${err.status}）：${err.message}` : '新建请求失败，请确认后端服务可用');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dispatch-panel">
      <div className="dp-t">新建待审主体</div>
      <div className="dp-s">
        这里登记的是<b>合作机构或专家主体</b>，不是成员，也不是服务资源本身。新建后状态为「待提交（draft）」
        —— 它只是一份待审档案，<b>尚未完成三级准入，因此不可约</b>；成员侧不会因为它出现在服务目录里就能预约。
      </div>
      <div className="grid2">
        <div className="field">
          <label className="field-label" htmlFor="rs-name">
            主体名称（必填，即营业执照 / 执业许可证上的名称）
          </label>
          <input
            id="rs-name"
            className="field-input"
            placeholder="例如：某三甲医院 / 某专家工作室"
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor="rs-type">
            主体类型（机构 / 专家）
          </label>
          <select id="rs-type" className="field-input" value={subjectType} onChange={(e) => setSubjectType(e.target.value)}>
            <option value="institution">机构</option>
            <option value="expert">专家</option>
          </select>
        </div>
        <div className="field">
          <label className="field-label" htmlFor="rs-category">
            服务分类（取值由服务端下发）
          </label>
          <select id="rs-category" className="field-input" value={category} onChange={(e) => setCategory(e.target.value)}>
            {categories.map((c) => (
              <option key={c.key} value={c.key}>
                {c.name}（{c.key}）
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="field-label" htmlFor="rs-city">
            城市（可选）
          </label>
          <input
            id="rs-city"
            className="field-input"
            placeholder="例如：北京"
            value={city}
            maxLength={20}
            onChange={(e) => setCity(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor="rs-license">
            执照号（可选；一级「主体资格」审核的对象）
          </label>
          <input
            id="rs-license"
            className="field-input"
            placeholder="营业执照 / 医疗机构执业许可证 / 金融牌照号"
            value={licenseNo}
            maxLength={64}
            onChange={(e) => setLicenseNo(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor="rs-license-expires">
            执照有效期（可选；只填日期即当天 23:59:59 到期）
          </label>
          <input
            id="rs-license-expires"
            className="field-input"
            type="date"
            value={licenseExpiresAt}
            onChange={(e) => setLicenseExpiresAt(e.target.value)}
          />
        </div>
      </div>
      <div className="field-hint">
        执照号缺失或有效期已过时，服务端会<b>拒绝记一级通过</b>（一级审的就是主体资格文件齐备且在有效期）；
        界面把这条前置条件摆在这里，是为了让「先补证照、再送审」成为默认动作。
      </div>
      {error && <div className="field-error">{error}</div>}
      <div className="row">
        <button type="button" className="btn primary sm" disabled={busy || name.trim().length === 0 || !category} onClick={() => void submit()}>
          {busy ? '正在新建…' : '新建待审主体'}
        </button>
        <span className="text-tiny muted">新建动作写入审计链（含工号），因此「谁登记了这个主体」可追溯。</span>
      </div>
    </div>
  );
}

export function ResourcesPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [tier, setTier] = useState('');
  const [keywordDraft, setKeywordDraft] = useState('');
  const [keyword, setKeyword] = useState('');
  const [toast, setToast] = useState<Toast | null>(null);

  // 筛选条件进 deps：改了筛选必须真的重新取数（否则控件点了没反应、表头却在说已筛选）
  const list = useLoad(
    (signal) => adminApi.resourceSubjects({ status, tier, keyword }, signal),
    [status, tier, keyword],
  );

  if (list.loading) return <LoadingBox text="正在加载被审主体…" />;

  if (list.error) {
    const err = list.error;
    if (err instanceof ApiError && err.status === 403) {
      return (
        <div>
          <PageHead title="资源准入审核" sub="403 · 权限拦截" />
          <Card>
            <PermissionBlock
              title="当前角色没有「resource:approve」权限"
              desc={`服务端返回：${err.message}。资源准入需要 resource:approve 权限点（资源审核员与超级管理员具备）；资源审核员的可见范围是 none —— 它审的是机构与专家资质，本来就不接触成员个体数据。界面不会把这次拦截渲染成「没有被审主体」。`}
            />
            <div className="sp12" />
            <button type="button" className="btn" onClick={list.reload}>
              重试
            </button>
          </Card>
        </div>
      );
    }
    return <ErrorBox message={errorText(err)} onRetry={list.reload} />;
  }

  if (!list.data) return <ErrorBox message="被审主体列表为空" onRetry={list.reload} />;

  const { items, summary, tiers, categories } = list.data;

  const handleCreated = (result: Toast): void => {
    setToast(result);
    list.reload();
    window.setTimeout(() => setToast(null), 8000);
  };

  return (
    <div>
      <PageHead
        title="资源准入审核"
        sub="三级准入（主体资格 → 专业与合规尽调 → 实地核验）+ 持续动态评估 + 不合格主体清退。只有三级全部通过（已准入 / 已上架）的主体才可约 —— 判据在服务端，界面不自行判定。"
        actions={
          <span className="badge ghost">
            共 {summary.total} 个被审主体 · 已上架可约 {summary.listed} 个
          </span>
        }
      />

      <Card
        title="准入状态分布"
        sub="计数取自接口 summary（与当前筛选一致），界面不自行累加"
      >
        <div className="grid4">
          <Kpi label="待提交（未审核）" value={summary.draft} tone="warn" delta="已登记但尚未完成任何一级，不可约" />
          <Kpi label="已准入（三级全通过）" value={summary.admitted} tone="e" delta="等待上架才会对成员开放" />
          <Kpi label="已上架可约" value={summary.listed} delta="成员侧可能出现号源" />
          <Kpi label="复评已逾期" value={summary.reviewOverdue} tone="danger" delta="持续动态评估未按时复评" />
          <Kpi label="临近复评（30 天内）" value={summary.reviewSoon} tone="warn" delta="提前量由服务端口径给出" />
          <Kpi label="已否决" value={summary.rejected} tone="danger" delta="终态：不得接入，需重新送审" />
          <Kpi label="已暂停" value={summary.suspended} tone="warn" delta="复评发现问题，可约资格已失效" />
          <Kpi label="已清退" value={summary.revoked} tone="danger" delta="终态：不再合作" />
        </div>
      </Card>

      <Card tight>
        <div className="chip-row">
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter.value || 'all-status'}
              type="button"
              className={`chip${status === filter.value ? ' on' : ''}`}
              onClick={() => setStatus(filter.value)}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <div className="chip-row">
          {TIER_FILTERS.map((filter) => (
            <button
              key={filter.value || 'all-tier'}
              type="button"
              className={`chip${tier === filter.value ? ' on' : ''}`}
              onClick={() => setTier(filter.value)}
            >
              {filter.label}
            </button>
          ))}
          <form
            className="row"
            style={{ marginLeft: 8 }}
            onSubmit={(e) => {
              e.preventDefault();
              setKeyword(keywordDraft);
            }}
          >
            <input
              className="field-input"
              style={{ minWidth: 220 }}
              placeholder="按名称 / 执照号 / 城市 / 分类搜索"
              value={keywordDraft}
              maxLength={60}
              onChange={(e) => setKeywordDraft(e.target.value)}
              aria-label="关键字搜索"
            />
            <button type="submit" className="btn sm">
              搜索
            </button>
            <button
              type="button"
              className="btn sm"
              onClick={() => {
                setKeywordDraft('');
                setKeyword('');
                setStatus('');
                setTier('');
              }}
            >
              清空筛选
            </button>
            <span className="text-tiny muted">
              当前筛选命中 {items.length} 条{keyword ? ` · 关键字「${keyword}」` : ''}
            </span>
          </form>
        </div>
      </Card>

      <Card tight>
        <CreateSubjectForm categories={categories} onDone={handleCreated} />
      </Card>

      <Card tight>
        {items.length === 0 && (
          <EmptyBox
            title="当前筛选下没有被审主体"
            desc="这可能只是「这个筛选条件没有命中」，而不是「平台没有登记任何主体」。换一个状态或级次再看一次，或用上方表单新建一个待审主体。"
          />
        )}

        {items.length > 0 && (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>主体名称</th>
                  <th>类型</th>
                  <th>分类</th>
                  <th>城市</th>
                  <th>准入状态</th>
                  <th>已完成到第几级</th>
                  <th>下次复评</th>
                  <th>复评紧迫度</th>
                  <th>是否可约</th>
                  <th style={{ textAlign: 'right' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const urgency = reviewUrgencyNotice(item);
                  const bookable = bookableNotice(item);
                  return (
                    <tr key={item.id}>
                      <td>
                        <b>{item.name}</b>
                        <div className="muted text-tiny mono-num">{item.id}</div>
                      </td>
                      <td>{item.subjectTypeLabel}</td>
                      <td>{item.categoryName}</td>
                      <td>{item.city || MISSING_TEXT}</td>
                      <td>
                        <Badge tone={admissionStatusTone(item.status)}>{item.statusLabel}</Badge>
                      </td>
                      <td>
                        <span className="mono-num">
                          {item.completedTiers} / {tiers.length}
                        </span>
                        <div className="muted text-tiny">{item.completedTierText}</div>
                      </td>
                      <td className="mono-num">
                        {item.nextReviewAt ? formatDateTime(item.nextReviewAt) : '未安排复评'}
                      </td>
                      <td>
                        <Badge tone={urgency.tone}>{urgency.text}</Badge>
                      </td>
                      <td>
                        <Badge tone={bookable.bookable ? 'ok' : bookable.tone === 'warn' ? 'warn' : 'danger'}>
                          {bookable.bookable ? '可约' : '不可约'}
                        </Badge>
                        <div className="muted text-tiny">
                          {bookable.bookable ? '三级准入已通过' : `原因：${bookable.reason}`}
                        </div>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button type="button" className="link" onClick={() => navigate(`/resources/${item.id}`)}>
                          查看详情
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="note-src">
        <b>三级审什么（口径随接口下发，界面不自己维护）</b>
        <br />
        {tiers.map((tierDef) => (
          <span key={tierDef.tier}>
            【{tierDef.label} · {tierDef.scope}】{tierDef.evidenceHint}
            <br />
          </span>
        ))}
        这三级的内容属于<b>平台侧定义</b>（原型只写了「三级准入审核 + 实地核验」，没有定义每一级审什么），
        因此只在接口与文档里维护一份。
      </div>

      <div className="note-src">
        <b>可约判据</b>
        <br />
        可约 = 状态为「已准入」或「已上架可约」，也就是<b>三级审核全部通过</b>。其余任何状态（含刚登记的
        「待提交」、以及已暂停 / 已清退 / 已否决）一律不可约 —— 这条判据由服务端的
        <span className="mono-num"> BOOKABLE_SQL</span> 单点给出，预约接口与资源目录都以它为准，
        不是靠界面隐藏按钮。「已准入」与「已上架可约」的区别只是：前者已通过三级审核但资源尚未登记上架。
      </div>

      <div className="note-src">
        <b>权限与可见范围</b>
        <br />
        读取本页需要 <span className="mono-num">resource:approve</span>。资源审核员（
        <span className="mono-num">reviewer</span>）的可见范围是 <span className="mono-num">none</span>：
        他能审机构与专家的资质，<b>看不到成员个体数据</b> —— 因此本页（以及清退时的预约清单）
        只出现工单标识与状态，不出现成员姓名。这不是界面漏了字段，而是权限模型的边界。
      </div>

      {toast && <div className={`toast ${toast.tone}`}>{toast.text}</div>}
      <div className="sp8" />
    </div>
  );
}
